import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const ROTATE_INTERVAL = 8000; // 8 seconds

const AdCarousel = ({ placement, className = '', country = '', state = '' }) => {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const impressionTracked = useRef(new Set());

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const params = new URLSearchParams({ placement });
        if (country) params.set('country', country);
        if (state) params.set('state', state);
        const res = await fetch(`${API}/api/ads/serve?${params}`);
        if (res.ok) {
          const data = await res.json();
          setAds(data.ads || []);
        }
      } catch {}
    };
    fetchAds();
  }, [placement, country, state]);

  // Rotation timer
  useEffect(() => {
    if (ads.length <= 1) return;
    const timer = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % ads.length);
        setFading(false);
      }, 400); // fade-out duration
    }, ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [ads.length]);

  // Track impressions
  useEffect(() => {
    if (ads.length === 0) return;
    const ad = ads[currentIndex];
    if (ad && !impressionTracked.current.has(ad.id)) {
      impressionTracked.current.add(ad.id);
      fetch(`${API}/api/ads/${ad.id}/impression`, { method: 'POST' }).catch(() => {});
    }
  }, [currentIndex, ads]);

  const handleClick = (ad) => {
    fetch(`${API}/api/ads/${ad.id}/click`, { method: 'POST' }).catch(() => {});
    if (ad.advertiser_url) {
      window.open(ad.advertiser_url, '_blank', 'noopener');
    }
  };

  if (ads.length === 0) return null;
  const ad = ads[currentIndex];
  if (!ad) return null;

  // Render based on placement type
  if (placement === 'find_honey_sponsored') {
    return (
      <div
        className={`transition-opacity duration-400 ${fading ? 'opacity-0' : 'opacity-100'} ${className}`}
        data-testid="ad-sponsored"
      >
        <div
          onClick={() => handleClick(ad)}
          className="bg-white rounded-2xl border-2 border-amber-300 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer relative"
        >
          <span className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
            Sponsored
          </span>
          <div className="flex items-start gap-4">
            {ad.image_url && (
              <img src={ad.image_url.startsWith('http') ? ad.image_url : `${API}${ad.image_url}`} alt={ad.title} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" onError={e => e.target.style.display='none'} />
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-stone-900 text-sm">{ad.advertiser_name}</h4>
              <p className="text-sm text-stone-600 mt-0.5 line-clamp-2">{ad.title}</p>
              {ad.description && <p className="text-xs text-stone-400 mt-1 line-clamp-1">{ad.description}</p>}
              <span className="inline-block mt-2 text-xs font-semibold text-amber-700 hover:text-amber-800">
                Visit Store →
              </span>
            </div>
          </div>
          {ads.length > 1 && (
            <div className="flex justify-center gap-1 mt-3">
              {ads.map((_, idx) => (
                <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentIndex ? 'bg-amber-500' : 'bg-stone-200'}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Banner / Sidebar format
  const isBanner = placement.includes('banner');
  return (
    <div
      className={`transition-opacity duration-400 ${fading ? 'opacity-0' : 'opacity-100'} ${className}`}
      data-testid={`ad-${placement}`}
    >
      <div
        onClick={() => handleClick(ad)}
        className={`relative cursor-pointer group overflow-hidden ${isBanner ? 'rounded-xl' : 'rounded-xl'}`}
        style={isBanner ? { width: '728px', maxWidth: '100%', height: '90px' } : {}}
      >
        {ad.image_url ? (
          <img
            src={ad.image_url.startsWith('http') ? ad.image_url : `${API}${ad.image_url}`}
            alt={ad.title}
            className="w-full h-full object-cover rounded-xl group-hover:brightness-95 transition-all"
            style={isBanner ? { width: '728px', maxWidth: '100%', height: '90px', objectFit: 'cover' } : {}}
          />
        ) : (
          <div className={`bg-gradient-to-r from-amber-50 via-amber-100 to-orange-50 border border-amber-200 rounded-xl flex items-center gap-4 ${isBanner ? 'h-[90px] px-6' : 'p-4'}`}>
            {ad.image_url === '' && ad.advertiser_logo && (
              <img src={ad.advertiser_logo.startsWith('http') ? ad.advertiser_logo : `${API}${ad.advertiser_logo}`} alt="" className="h-12 w-12 rounded-lg object-cover flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-stone-800 text-sm">{ad.advertiser_name}</p>
              <p className="text-xs text-stone-600 mt-0.5 line-clamp-1">{ad.title}</p>
              {ad.description && <p className="text-[11px] text-stone-400 mt-0.5 line-clamp-1">{ad.description}</p>}
            </div>
            <span className="text-xs font-semibold text-amber-700 whitespace-nowrap flex-shrink-0">Visit →</span>
          </div>
        )}
        <span className="absolute top-1.5 right-1.5 bg-black/40 text-white text-[9px] font-medium px-1.5 py-0.5 rounded">
          Ad
        </span>
      </div>
      {ads.length > 1 && (
        <div className="flex justify-center gap-1 mt-2">
          {ads.map((_, idx) => (
            <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentIndex ? 'bg-amber-500' : 'bg-stone-200'}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdCarousel;
